import React, { useState, useMemo } from "react";
import { Row, Col, Card, CardBody, Input, Button, Spinner, Badge, Modal, ModalHeader, ModalBody, ListGroup, ListGroupItem, Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import useFacebookPageSearch from "../query/hooks/useFacebookPageSearch";
import useInstagramSearch from "../query/hooks/useInstagramSearch";
import useTrackedFacebookPages, { useTrackFacebookPage, useUntrackFacebookPage } from "../query/hooks/useTrackedFacebookPages";
import useGetSponsorObjects from "../query/hooks/useGetSponsorObjects";

const TrackedAccounts = () => {
  const { t } = useTranslation();
  const [notification, setNotification] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selectedSponsorObject, setSelectedSponsorObject] = useState(null);
  const [partnerFilterSearch, setPartnerFilterSearch] = useState("");
  const [partnerFilterOpen, setPartnerFilterOpen] = useState(false);

  // Track modal state
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [trackModalPage, setTrackModalPage] = useState(null);
  const [trackModalSearch, setTrackModalSearch] = useState("");

  // Track which page is being untracked (for spinner)
  const [untrackingPageId, setUntrackingPageId] = useState(null);

  // Track which partner is being selected for tracking (for spinner)
  const [trackingPartnerId, setTrackingPartnerId] = useState(null);

  // Instagram link modal state (for selecting which Facebook page to link to)
  const [instagramLinkModalOpen, setInstagramLinkModalOpen] = useState(false);
  const [instagramLinkModalAccount, setInstagramLinkModalAccount] = useState(null);
  const [instagramLinkModalTenantId, setInstagramLinkModalTenantId] = useState(null);
  const [selectedFacebookPageId, setSelectedFacebookPageId] = useState(null);

  // Fetch sponsor objects for filter and track assignment
  const { data: sponsorObjects } = useGetSponsorObjects();

  // Handle search submission
  const handleSearch = () => {
    setSubmittedQuery(searchQuery);
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const { data: facebookResults, isLoading: isFacebookSearching, isFetching: isFacebookFetching } = useFacebookPageSearch(submittedQuery);
  const { data: instagramResults, isLoading: isInstagramSearching, isFetching: isInstagramFetching } = useInstagramSearch(submittedQuery);
  const { data: trackedPages, isLoading: isLoadingTracked } = useTrackedFacebookPages(selectedSponsorObject?.id);
  const { data: allTrackedPages } = useTrackedFacebookPages(null); // All tracked pages for filtering search results
  const trackPageMutation = useTrackFacebookPage();
  const untrackPageMutation = useUntrackFacebookPage();

  // Get set of all tracked page IDs for quick lookup
  const trackedPageIds = useMemo(() => {
    const ids = new Set();
    allTrackedPages?.data?.forEach(page => ids.add(page.page_id));
    return ids;
  }, [allTrackedPages]);

  // Get set of all tracked Instagram account IDs for quick lookup
  const trackedInstagramIds = useMemo(() => {
    const ids = new Set();
    // Instagram accounts linked to Facebook pages
    allTrackedPages?.data?.forEach(page => {
      page.instagram_accounts?.forEach(ig => ids.add(ig.account_id));
    });
    // Unlinked Instagram accounts
    allTrackedPages?.unlinked_instagram?.forEach(ig => ids.add(ig.account_id));
    return ids;
  }, [allTrackedPages]);

  // Combine Facebook and Instagram search results, excluding already tracked pages
  const searchResults = React.useMemo(() => {
    const fbData = facebookResults?.data || [];
    const igData = instagramResults?.data || [];
    // Filter out tracked Facebook pages by page_id, and Instagram accounts by is_tracked flag or account_id
    const combinedData = [...fbData, ...igData].filter(page => {
      if (page.platform === 'instagram') {
        // Use is_tracked flag from backend if available, otherwise check our tracked IDs set
        return page.is_tracked === false || (!page.is_tracked && !trackedInstagramIds.has(page.id));
      }
      return !trackedPageIds.has(page.id);
    });
    return {
      data: combinedData,
      hashtag: instagramResults?.hashtag,
      type: instagramResults?.type
    };
  }, [facebookResults, instagramResults, trackedPageIds, trackedInstagramIds]);

  // Combined loading states
  const isSearching = isFacebookSearching || isInstagramSearching;
  const isFetching = isFacebookFetching || isInstagramFetching;

  // Filter sponsor objects for partner filter dropdown
  const filteredPartnerOptions = useMemo(() => {
    if (!sponsorObjects) return [];
    if (!partnerFilterSearch.trim()) return sponsorObjects;
    return sponsorObjects.filter(so =>
      so.name.toLowerCase().includes(partnerFilterSearch.toLowerCase())
    );
  }, [sponsorObjects, partnerFilterSearch]);

  // Filter sponsor objects for track modal
  const filteredTrackModalOptions = useMemo(() => {
    if (!sponsorObjects) return [];
    if (!trackModalSearch.trim()) return sponsorObjects;
    return sponsorObjects.filter(so =>
      so.name.toLowerCase().includes(trackModalSearch.toLowerCase())
    );
  }, [sponsorObjects, trackModalSearch]);

  // Open track modal for a page
  const openTrackModal = (page) => {
    setTrackModalPage(page);
    setTrackModalSearch("");
    setTrackModalOpen(true);
  };

  // Close track modal
  const closeTrackModal = () => {
    setTrackModalOpen(false);
    setTrackModalPage(null);
    setTrackModalSearch("");
    setTrackingPartnerId(null);
  };

  // Open Instagram link modal (for selecting which Facebook page to link to)
  const openInstagramLinkModal = (account, tenantId) => {
    setInstagramLinkModalAccount(account);
    setInstagramLinkModalTenantId(tenantId);
    setSelectedFacebookPageId(null);
    setInstagramLinkModalOpen(true);
  };

  // Close Instagram link modal
  const closeInstagramLinkModal = () => {
    setInstagramLinkModalOpen(false);
    setInstagramLinkModalAccount(null);
    setInstagramLinkModalTenantId(null);
    setSelectedFacebookPageId(null);
  };

  const handleTrackPage = (page, tenantId = null) => {
    // Use selected sponsor object if set and no specific tenantId provided
    const targetTenantId = tenantId || selectedSponsorObject?.id || null;

    // For Instagram accounts, always show the modal to let user choose whether to link to a Facebook page
    if (page.platform === 'instagram') {
      openInstagramLinkModal(page, targetTenantId);
      closeTrackModal();
      return;
    }

    setTrackingPartnerId(targetTenantId);
    trackPageMutation.mutate(
      {
        pageId: page.id,
        name: page.name,
        pictureUrl: page.picture?.url || page.picture?.data?.url,
        tenantId: targetTenantId,
        platform: page.platform || 'facebook'
      },
      {
        onSuccess: (response) => {
          setTrackingPartnerId(null);
          closeTrackModal();
          closeInstagramLinkModal();
          if (response?.data?.message?.toLowerCase().includes('already tracked')) {
            setNotification({ type: 'warning', message: t("account_already_tracked") });
            setTimeout(() => setNotification(null), 5000);
          }
        },
        onError: (error) => {
          console.error("Failed to track page:", error);
          setTrackingPartnerId(null);
        },
      }
    );
  };

  // Handle tracking Instagram with specific Facebook page selection
  const handleTrackInstagramWithFacebookPage = (facebookPageId) => {
    if (!instagramLinkModalAccount) return;

    setSelectedFacebookPageId(facebookPageId);
    trackPageMutation.mutate(
      {
        pageId: instagramLinkModalAccount.id,
        name: instagramLinkModalAccount.name,
        // The handle is what posts are collected by; name is only the display name
        username: instagramLinkModalAccount.username,
        pictureUrl: instagramLinkModalAccount.picture?.url || instagramLinkModalAccount.picture?.data?.url,
        tenantId: instagramLinkModalTenantId,
        platform: 'instagram',
        facebookPageId: facebookPageId // Pass specific Facebook page to link to
      },
      {
        onSuccess: (response) => {
          setSelectedFacebookPageId(null);
          closeInstagramLinkModal();
          if (response?.data?.message?.toLowerCase().includes('already tracked')) {
            setNotification({ type: 'warning', message: t("account_already_tracked") });
            setTimeout(() => setNotification(null), 5000);
          }
        },
        onError: (error) => {
          console.error("Failed to track Instagram account:", error);
          setSelectedFacebookPageId(null);
        },
      }
    );
  };

  const handleUntrackPage = (pageId) => {
    setUntrackingPageId(pageId);
    untrackPageMutation.mutate(pageId, {
      onSuccess: () => {
        setUntrackingPageId(null);
      },
      onError: (error) => {
        console.error("Failed to untrack page:", error);
        setUntrackingPageId(null);
      },
    });
  };

  // Check if we have sponsor objects for sub-tenant selection
  const hasSponsorObjects = sponsorObjects && sponsorObjects.length > 0;

  return (
    <>
      {notification && (
        <Alert color={notification.type} toggle={() => setNotification(null)} className="mb-3">
          {notification.message}
        </Alert>
      )}
      <Row className="mb-4" style={{ overflow: 'visible' }}>
        <Col sm={12} md={8} lg={6}>
          <div className="d-flex gap-2">
            <div className="position-relative flex-grow-1">
              <Input
                type="text"
                placeholder={t("search_instagram_facebook")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="bg-card"
                style={{ color: 'var(--text-primary)' }}
              />
              {isFetching && submittedQuery && (
                <Spinner
                  size="sm"
                  className="position-absolute"
                  style={{ right: "10px", top: "10px" }}
                />
              )}
            </div>
            <Button
              color="primary"
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
            >
              <i className="mdi mdi-magnify"></i> {t("search")}
            </Button>
          </div>
        </Col>
        {hasSponsorObjects && (
          <Col sm={12} md={4} lg={3} className="mt-3 mt-md-0">
            <div className="position-relative">
              <Button
                color="primary"
                className="w-100 text-start d-flex justify-content-between align-items-center"
                onClick={() => setPartnerFilterOpen(!partnerFilterOpen)}
                style={{ color: 'var(--text-primary)' }}
              >
                <span className="text-truncate">
                  {selectedSponsorObject ? selectedSponsorObject.name : t("all_partners")}
                </span>
                <i className={`mdi mdi-chevron-${partnerFilterOpen ? 'up' : 'down'}`}></i>
              </Button>
              {partnerFilterOpen && (
                <div
                  className="position-absolute w-100 rounded mt-1"
                  style={{
                    zIndex: 1050,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    backgroundColor: 'var(--background-secondary)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div className="p-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <Input
                      type="text"
                      placeholder={t("search_partners")}
                      value={partnerFilterSearch}
                      onChange={(e) => setPartnerFilterSearch(e.target.value)}
                      style={{ color: 'var(--text-primary)' }}
                      autoFocus
                    />
                  </div>
                  <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <ListGroup flush>
                      <ListGroupItem
                        action
                        active={!selectedSponsorObject}
                        onClick={() => {
                          setSelectedSponsorObject(null);
                          setPartnerFilterOpen(false);
                          setPartnerFilterSearch("");
                        }}
                        style={{
                          backgroundColor: !selectedSponsorObject ? 'var(--primary)' : 'var(--background-secondary)',
                          color: !selectedSponsorObject ? '#fff' : 'var(--text-primary)',
                          cursor: 'pointer',
                          border: 'none'
                        }}
                      >
                        {t("all_partners")}
                      </ListGroupItem>
                      {filteredPartnerOptions.map((so) => (
                        <ListGroupItem
                          key={so.id}
                          action
                          active={selectedSponsorObject?.id === so.id}
                          onClick={() => {
                            setSelectedSponsorObject(so);
                            setPartnerFilterOpen(false);
                            setPartnerFilterSearch("");
                          }}
                          style={{
                            backgroundColor: selectedSponsorObject?.id === so.id ? 'var(--primary)' : 'var(--background-secondary)',
                            color: selectedSponsorObject?.id === so.id ? '#fff' : 'var(--text-primary)',
                            cursor: 'pointer',
                            border: 'none'
                          }}
                        >
                          {so.name}
                        </ListGroupItem>
                      ))}
                      {filteredPartnerOptions.length === 0 && (
                        <ListGroupItem
                          style={{
                            backgroundColor: 'var(--background-secondary)',
                            color: 'var(--text-secondary)',
                            border: 'none',
                            textAlign: 'center'
                          }}
                        >
                          {t("no_results_found")}
                        </ListGroupItem>
                      )}
                    </ListGroup>
                  </div>
                </div>
              )}
            </div>
          </Col>
        )}
      </Row>

      {/* Search Results */}
      {submittedQuery && searchResults?.data && searchResults.data.length > 0 && (
        <Row className="mb-4">
          <Col sm={12}>
            <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>
              {searchResults.type === 'hashtag' ? (
                <>
                  <i className="mdi mdi-pound"></i> {searchResults.hashtag} - {t("recent_posts")} ({searchResults.data.length})
                </>
              ) : (
                <>
                  {t("search_results")} ({searchResults.data.length})
                </>
              )}
            </h5>
            <Row>
              {searchResults.type === 'hashtag' ? (
                // Render hashtag media posts
                searchResults.data.map((media) => (
                  <Col key={media.id} sm={12} md={6} lg={4} className="mb-4">
                    <Card className="mini-stat bg-card h-100">
                      <CardBody className="p-0">
                        {media.media_type === 'VIDEO' && media.thumbnail_url ? (
                          <img
                            src={media.thumbnail_url}
                            alt={media.caption?.substring(0, 50) || 'Instagram post'}
                            style={{
                              width: "100%",
                              height: "250px",
                              objectFit: "cover"
                            }}
                          />
                        ) : media.media_url ? (
                          <img
                            src={media.media_url}
                            alt={media.caption?.substring(0, 50) || 'Instagram post'}
                            style={{
                              width: "100%",
                              height: "250px",
                              objectFit: "cover"
                            }}
                          />
                        ) : null}
                        <div className="p-3">
                          <div className="d-flex align-items-center mb-2">
                            <strong style={{ color: 'var(--text-primary)' }}>
                              @{media.username}
                            </strong>
                            <Badge color="danger" className="ms-2" style={{ fontSize: '0.7rem' }}>
                              <i className="mdi mdi-instagram"></i>
                            </Badge>
                          </div>
                          {media.caption && (
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                              {media.caption.length > 100 ? media.caption.substring(0, 100) + '...' : media.caption}
                            </p>
                          )}
                          <div className="d-flex gap-3 mb-2" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            {media.like_count !== undefined && (
                              <span>
                                <i className="mdi mdi-heart"></i> {media.like_count.toLocaleString()}
                              </span>
                            )}
                            {media.comments_count !== undefined && (
                              <span>
                                <i className="mdi mdi-comment"></i> {media.comments_count.toLocaleString()}
                              </span>
                            )}
                          </div>
                          {media.permalink && (
                            <a
                              href={media.permalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary w-100"
                            >
                              <i className="mdi mdi-instagram"></i> {t("view_post")}
                            </a>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))
              ) : (
                // Render account results (Facebook pages and Instagram accounts)
                searchResults.data.map((page) => (
                <Col key={page.id} sm={12} md={6} lg={4} className="mb-4">
                  <Card className="mini-stat bg-card text-white h-100">
                    <CardBody>
                      <div className="text-center mb-3">
                        {(page.picture?.url || page.picture?.data?.url) ? (
                          <img
                            src={page.picture?.url || page.picture?.data?.url}
                            alt={page.name}
                            className="rounded-circle mb-2"
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "cover",
                              border: "3px solid rgba(255, 255, 255, 0.1)"
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-circle mb-2 d-flex align-items-center justify-content-center mx-auto"
                            style={{
                              width: "80px",
                              height: "80px",
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              border: "3px solid rgba(255, 255, 255, 0.1)"
                            }}
                          >
                            <i className={`mdi ${page.platform === 'instagram' ? 'mdi-instagram' : 'mdi-facebook'}`} style={{ fontSize: "32px", opacity: 0.5 }}></i>
                          </div>
                        )}
                        <h6 className="mb-1 d-flex align-items-center justify-content-center" style={{ color: 'var(--text-primary)' }}>
                          {page.name}
                          {page.verification_status === "blue_verified" && (
                            <i className="mdi mdi-check-decagram text-info ms-2" style={{ fontSize: '1rem' }}></i>
                          )}
                        </h6>
                        <div className="mb-2">
                          {page.platform === 'instagram' ? (
                            <Badge color="danger" style={{ fontSize: '0.7rem' }}>
                              <i className="mdi mdi-instagram"></i> Instagram
                            </Badge>
                          ) : (
                            <Badge color="primary" style={{ fontSize: '0.7rem' }}>
                              <i className="mdi mdi-facebook"></i> Facebook
                            </Badge>
                          )}
                        </div>
                        {page.username && page.platform === 'instagram' && (
                          <p className="mb-1" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                            @{page.username}
                          </p>
                        )}
                        {page.followers_count && (
                          <p className="mb-1" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                            <i className="mdi mdi-account-multiple"></i> {page.followers_count.toLocaleString()} {t("followers")}
                          </p>
                        )}
                        {page.location?.city && (
                          <p className="mb-0" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                            <i className="mdi mdi-map-marker"></i> {page.location.city}
                            {page.location.country && `, ${page.location.country}`}
                          </p>
                        )}
                      </div>

                      {page.posts && page.posts.length > 0 && (
                        <div className="mb-3">
                          <p className="mb-2" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                            <i className="mdi mdi-post"></i> {t("recent_posts")}:
                          </p>
                          <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                            {page.posts.slice(0, 2).map((post) => (
                              <div key={post.id} className="mb-2 p-2" style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: "4px" }}>
                                <div className="d-flex align-items-start">
                                  {post.full_picture && (
                                    <img
                                      src={post.full_picture}
                                      alt="Post"
                                      className="me-2"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        flexShrink: 0
                                      }}
                                      onError={(e) => {
                                        console.log('Failed to load image:', post.full_picture);
                                        e.target.style.display = 'none';
                                      }}
                                      onLoad={() => {
                                        console.log('Successfully loaded image:', post.full_picture);
                                      }}
                                    />
                                  )}
                                  <p
                                    className="mb-0 flex-grow-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      color: 'var(--text-primary)',
                                      display: "-webkit-box",
                                      WebkitLineClamp: "2",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {post.message || t("no_message")}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-auto">
                        <a
                          href={page.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {t("view_page")} <i className="mdi mdi-open-in-new"></i>
                        </a>
                        {hasSponsorObjects ? (
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => openTrackModal(page)}
                            disabled={trackPageMutation.isPending}
                          >
                            <i className="mdi mdi-plus"></i> {t("track")}
                          </Button>
                        ) : (
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleTrackPage(page)}
                            disabled={trackPageMutation.isPending}
                          >
                            <i className="mdi mdi-plus"></i> {t("track")}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
              )}
            </Row>
          </Col>
        </Row>
      )}

      {/* Why Instagram returned nothing, shown next to Facebook results */}
      {submittedQuery && !isSearching && instagramResults?.reason && searchResults?.data?.length > 0 && (
        <Row className="mb-4">
          <Col sm={12}>
            <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>
              <i className="mdi mdi-instagram me-1"></i>
              {t(`tracked_${instagramResults.reason}`, { username: instagramResults.username })}
            </p>
          </Col>
        </Row>
      )}

      {/* No search results */}
      {submittedQuery && searchResults?.data && searchResults.data.length === 0 && !isSearching && (
        <Row className="mb-4">
          <Col sm={12}>
            <Card className="mini-stat bg-card text-white">
              <CardBody>
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ minHeight: "200px" }}
                >
                  <i className="ti-search mb-3" style={{ fontSize: "48px", opacity: 0.5, color: 'var(--text-secondary)' }}></i>
                  <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>{t("no_results_found")}</p>
                  {instagramResults?.reason && (
                    <p className="mb-0 mt-2" style={{ color: 'var(--text-secondary)' }}>
                      {t(`tracked_${instagramResults.reason}`, { username: instagramResults.username })}
                    </p>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tracked Pages */}
      {!isLoadingTracked && ((trackedPages?.data && trackedPages.data.length > 0) || (trackedPages?.unlinked_instagram && trackedPages.unlinked_instagram.length > 0)) && (
        <Row className="mt-4">
          <Col sm={12}>
            <h5 className="mb-3 d-flex align-items-center" style={{ color: 'var(--text-primary)' }}>
              <span>{t("tracked_social_media")}</span>
              {selectedSponsorObject && (
                <Badge color="info" className="ms-3 ml-2" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                  {selectedSponsorObject.name}
                </Badge>
              )}
            </h5>
            <Row>
              {trackedPages.data.map((page) => (
                <Col key={page.id} sm={12} md={6} lg={4} className="mb-4">
                  <Card className="mini-stat bg-card text-white h-100">
                    <CardBody>
                      {/* Facebook Page Header */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center">
                          {page.profile_pic_url ? (
                            <img
                              src={page.profile_pic_url}
                              alt={page.name}
                              className="rounded-circle me-3"
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                border: "2px solid rgba(255, 255, 255, 0.1)"
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "50px",
                                height: "50px",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                border: "2px solid rgba(255, 255, 255, 0.1)"
                              }}
                            >
                              <i className="mdi mdi-facebook" style={{ fontSize: "24px", opacity: 0.5 }}></i>
                            </div>
                          )}
                          <div>
                            <h6 className="mb-0" style={{ color: 'var(--text-primary)' }}>{page.name}</h6>
                            <p className="mb-0" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                              <a
                                href={`https://www.facebook.com/${page.page_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                              >
                                <i className="mdi mdi-facebook"></i> Facebook Page
                                <i className="mdi mdi-open-in-new ms-1" style={{ fontSize: '0.75rem' }}></i>
                              </a>
                            </p>
                            {page.tenant_name && !selectedSponsorObject && hasSponsorObjects && (
                              <Badge color="info" className="mt-1" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                                {page.tenant_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => handleUntrackPage(page.page_id)}
                          disabled={untrackingPageId === page.page_id}
                          title={page.instagram_accounts?.length > 0 ? t("untrack_page_and_instagram") : t("untrack")}
                        >
                          {untrackingPageId === page.page_id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="mdi mdi-close"></i>
                          )}
                        </Button>
                      </div>

                      {/* Linked Instagram Accounts */}
                      {page.instagram_accounts && page.instagram_accounts.length > 0 && (
                        <div
                          className="mt-2 pt-2"
                          style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
                        >
                          <p className="mb-2" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <i className="mdi mdi-link-variant me-1"></i>
                            {t("linked_instagram_accounts")}
                          </p>
                          {page.instagram_accounts.map((igAccount) => (
                            <div
                              key={igAccount.id}
                              className="d-flex justify-content-between align-items-center p-2 mb-2"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                borderLeft: '3px solid #E1306C'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                {igAccount.profile_pic_url ? (
                                  <img
                                    src={igAccount.profile_pic_url}
                                    alt={igAccount.username}
                                    className="rounded-circle me-2"
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      objectFit: "cover"
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      backgroundColor: "rgba(255, 255, 255, 0.1)"
                                    }}
                                  >
                                    <i className="mdi mdi-instagram" style={{ fontSize: "16px", color: '#E1306C' }}></i>
                                  </div>
                                )}
                                <div>
                                  <a
                                    href={`https://www.instagram.com/${igAccount.username}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--text-primary)', textDecoration: 'none', fontSize: '0.9rem' }}
                                  >
                                    @{igAccount.username}
                                    <i className="mdi mdi-open-in-new ms-1" style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}></i>
                                  </a>
                                </div>
                              </div>
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                onClick={() => handleUntrackPage(igAccount.account_id)}
                                disabled={untrackingPageId === igAccount.account_id}
                                style={{ padding: '0.15rem 0.4rem', fontSize: '0.75rem' }}
                              >
                                {untrackingPageId === igAccount.account_id ? (
                                  <Spinner size="sm" style={{ width: '0.75rem', height: '0.75rem' }} />
                                ) : (
                                  <i className="mdi mdi-close"></i>
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              ))}
              {/* Unlinked Instagram Accounts - displayed in same grid */}
              {trackedPages?.unlinked_instagram?.map((igAccount) => (
                <Col key={`ig-${igAccount.id}`} sm={12} md={6} lg={4} className="mb-4">
                  <Card className="mini-stat bg-card text-white h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-center">
                          {igAccount.profile_pic_url ? (
                            <img
                              src={igAccount.profile_pic_url}
                              alt={igAccount.username}
                              className="rounded-circle me-3"
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                border: "2px solid rgba(255, 255, 255, 0.1)"
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "50px",
                                height: "50px",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                border: "2px solid rgba(255, 255, 255, 0.1)"
                              }}
                            >
                              <i className="mdi mdi-instagram" style={{ fontSize: "24px", color: '#E1306C' }}></i>
                            </div>
                          )}
                          <div>
                            <h6 className="mb-0" style={{ color: 'var(--text-primary)' }}>@{igAccount.username}</h6>
                            <p className="mb-0" style={{ fontSize: "0.85rem", color: 'var(--text-secondary)' }}>
                              <a
                                href={`https://www.instagram.com/${igAccount.username}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                              >
                                <i className="mdi mdi-instagram" style={{ color: '#E1306C' }}></i> Instagram
                                <i className="mdi mdi-open-in-new ms-1" style={{ fontSize: '0.75rem' }}></i>
                              </a>
                            </p>
                            {igAccount.tenant_name && !selectedSponsorObject && hasSponsorObjects && (
                              <Badge color="info" className="mt-1" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                                {igAccount.tenant_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => handleUntrackPage(igAccount.account_id)}
                          disabled={untrackingPageId === igAccount.account_id}
                          title={t("untrack")}
                        >
                          {untrackingPageId === igAccount.account_id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="mdi mdi-close"></i>
                          )}
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* Empty state when no search and no tracked pages */}
      {!searchQuery && (!trackedPages?.data || trackedPages.data.length === 0) && (!trackedPages?.unlinked_instagram || trackedPages.unlinked_instagram.length === 0) && !isLoadingTracked && (
        <Row>
          <Col sm={12}>
            <Card className="mini-stat bg-card text-white">
              <CardBody>
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ minHeight: "300px" }}
                >
                  <i className="ti-search mb-3" style={{ fontSize: "48px", opacity: 0.5, color: 'var(--text-secondary)' }}></i>
                  <h5 className="mb-2" style={{ color: 'var(--text-secondary)' }}>{t("discover_social_accounts")}</h5>
                  <p className="text-center mb-0" style={{ color: 'var(--text-secondary)' }}>
                    {t("search_track_social_accounts")}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Track Page Modal */}
      <Modal isOpen={trackModalOpen} toggle={closeTrackModal} centered>
        <ModalHeader toggle={closeTrackModal}>
          {t("assign_to_partner")}
        </ModalHeader>
        <ModalBody>
          {trackModalPage && (
            <div className="mb-3 d-flex align-items-center">
              {(trackModalPage.picture?.url || trackModalPage.picture?.data?.url) ? (
                <img
                  src={trackModalPage.picture?.url || trackModalPage.picture?.data?.url}
                  alt={trackModalPage.name}
                  className="rounded-circle me-3"
                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                  style={{ width: "50px", height: "50px", backgroundColor: "var(--background-secondary)" }}
                >
                  <i className={`mdi ${trackModalPage.platform === 'instagram' ? 'mdi-instagram' : 'mdi-facebook'}`} style={{ fontSize: "24px", color: 'var(--text-secondary)' }}></i>
                </div>
              )}
              <div>
                <h6 className="mb-0" style={{ color: 'var(--text-primary)' }}>{trackModalPage.name}</h6>
                <small style={{ color: 'var(--text-secondary)' }}>
                  {trackModalPage.platform === 'instagram' ? 'Instagram' : 'Facebook'}
                </small>
              </div>
            </div>
          )}
          <div className="position-relative mb-3">
            <i
              className="mdi mdi-magnify position-absolute"
              style={{
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-secondary)',
                fontSize: '1.1rem'
              }}
            />
            <Input
              type="text"
              placeholder={t("search_partners")}
              value={trackModalSearch}
              onChange={(e) => setTrackModalSearch(e.target.value)}
              style={{ paddingLeft: '36px' }}
            />
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredTrackModalOptions.map((so) => (
              <div
                key={so.id}
                onClick={() => !trackingPartnerId && handleTrackPage(trackModalPage, so.id)}
                className="d-flex justify-content-between align-items-center"
                style={{
                  padding: '10px 12px',
                  cursor: trackingPartnerId ? 'default' : 'pointer',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  opacity: trackingPartnerId && trackingPartnerId !== so.id ? 0.5 : 1,
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!trackingPartnerId) e.currentTarget.style.backgroundColor = 'var(--background-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span>{so.name}</span>
                {trackingPartnerId === so.id && (
                  <Spinner size="sm" />
                )}
              </div>
            ))}
            {filteredTrackModalOptions.length === 0 && (
              <p className="text-center mb-0 py-3" style={{ color: 'var(--text-secondary)' }}>
                {t("no_results_found")}
              </p>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Instagram Link Modal - Select which Facebook page to link to */}
      <Modal isOpen={instagramLinkModalOpen} toggle={closeInstagramLinkModal} centered>
        <ModalHeader toggle={closeInstagramLinkModal}>
          {t("link_instagram_to_facebook")}
        </ModalHeader>
        <ModalBody>
          {instagramLinkModalAccount && (
            <div className="mb-3 d-flex align-items-center">
              {(instagramLinkModalAccount.picture?.url || instagramLinkModalAccount.picture?.data?.url) ? (
                <img
                  src={instagramLinkModalAccount.picture?.url || instagramLinkModalAccount.picture?.data?.url}
                  alt={instagramLinkModalAccount.name}
                  className="rounded-circle me-3"
                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                  style={{ width: "50px", height: "50px", backgroundColor: "var(--background-secondary)" }}
                >
                  <i className="mdi mdi-instagram" style={{ fontSize: "24px", color: '#E1306C' }}></i>
                </div>
              )}
              <div>
                <h6 className="mb-0" style={{ color: 'var(--text-primary)' }}>@{instagramLinkModalAccount.username || instagramLinkModalAccount.name}</h6>
                <small style={{ color: 'var(--text-secondary)' }}>Instagram</small>
              </div>
            </div>
          )}

          {/* Get Facebook pages for the target tenant */}
          {(() => {
            const targetFacebookPages = allTrackedPages?.data?.filter(p =>
              p.type === 'facebook' && (instagramLinkModalTenantId ? p.sponsor_object_id === instagramLinkModalTenantId : true)
            ) || [];

            return (
              <>
                {targetFacebookPages.length > 0 && (
                  <>
                    <p className="mb-3" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {t("select_facebook_page_to_link")}
                    </p>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="mb-3">
                      <div className="d-grid gap-2">
                        {targetFacebookPages.map((fbPage) => (
                          <Button
                            key={fbPage.id}
                            color="primary"
                            outline
                            onClick={() => handleTrackInstagramWithFacebookPage(fbPage.id)}
                            disabled={selectedFacebookPageId !== null}
                            className="text-start d-flex justify-content-between align-items-center"
                          >
                            <div className="d-flex align-items-center">
                              {fbPage.profile_pic_url ? (
                                <img
                                  src={fbPage.profile_pic_url}
                                  alt={fbPage.name}
                                  className="rounded-circle me-2"
                                  style={{ width: "32px", height: "32px", objectFit: "cover" }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                                  style={{ width: "32px", height: "32px", backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                                >
                                  <i className="mdi mdi-facebook" style={{ fontSize: "16px" }}></i>
                                </div>
                              )}
                              <span>{fbPage.name}</span>
                            </div>
                            {selectedFacebookPageId === fbPage.id && (
                              <Spinner size="sm" />
                            )}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <hr style={{ borderColor: 'var(--border-color)' }} />
                  </>
                )}

                {/* Track without linking option */}
                <Button
                  color="primary"
                  onClick={() => handleTrackInstagramWithFacebookPage(0)}
                  disabled={selectedFacebookPageId !== null}
                  className="w-100 d-flex justify-content-between align-items-center"
                >
                  <span>{t("track_without_linking")}</span>
                  {selectedFacebookPageId === 0 && (
                    <Spinner size="sm" />
                  )}
                </Button>
                {targetFacebookPages.length === 0 && (
                  <p className="mt-2 mb-0 text-center" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {t("no_facebook_pages_optional")}
                  </p>
                )}
              </>
            );
          })()}
        </ModalBody>
      </Modal>
    </>
  );
};

export default TrackedAccounts;
